import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { GeneralDialog, GeneralInput } from 'sharedComponents';
import { IconInfo } from 'svgIcons/MotionPortalIcons';
import { removeUnallowedCharsFromString } from 'helpers/validate';
import colors from 'theme/_colors.scss';
import { ADD_GEARBOX_DEFAULTS } from '../../constants';
import { MAX_INPUT_CHAR_LENGTH, MAX_SPEED_RATIO_LENGTH } from 'helpers/constants';
import './AddGearboxDialog.scss';

/**
 * AddGearboxDialog - Dialog for adding an unmonitored gearbox asset
 * Pre-fills Organization, Site, Asset type, and Sub-type from powertrain context
 * User enters Asset name (mandatory) and optional Asset access group
 */
const AddGearboxDialog = ({
    show,
    onClose,
    onAdd,
    organization = '',
    site = ''
}) => {
    const { t: translate } = useTranslation();
    const [assetName, setAssetName] = useState('');
    const [outputSpeedRatio, setOutputSpeedRatio] = useState('');
    const [touched, setTouched] = useState(false);
    const [specialCharError, setSpecialCharError] = useState('');


    const validationError = useMemo(() => {
        if (!touched) {
            return null;
        }
        if (specialCharError) {
            return specialCharError;
        }
        if (!assetName || assetName.trim() === '') {
            return translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AddGearbox_NameRequired');
        }
        if (assetName.trim()?.length > ADD_GEARBOX_DEFAULTS.MAX_ASSET_NAME_LENGTH) {
            return translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AddGearbox_NameTooLong');
        }
        return null;
    }, [assetName, touched, translate, specialCharError]);

    const isValid = useMemo(() => {
        return assetName && assetName.trim()?.length > 0 && assetName.trim()?.length <= ADD_GEARBOX_DEFAULTS.MAX_ASSET_NAME_LENGTH;
    }, [assetName]);

    const handleAdd = useCallback(() => {
        setTouched(true);
        if (!isValid) {
            return;
        }

        onAdd({
            assetName: assetName.trim(),
            outputSpeedRatio: outputSpeedRatio ? outputSpeedRatio.trim() : null,
            assetType: ADD_GEARBOX_DEFAULTS.ASSET_TYPE,
            subType: ADD_GEARBOX_DEFAULTS.SUB_TYPE,
            organization,
            site
        });

        // Reset form
        setAssetName('');
        setOutputSpeedRatio('');
        setTouched(false);
        setSpecialCharError('');
    }, [assetName, outputSpeedRatio, isValid, onAdd, organization, site]);


    const handleClose = useCallback(() => {
        // Reset form on close
        setAssetName('');
        setOutputSpeedRatio('');
        setTouched(false);
        setSpecialCharError('');
        onClose();
    }, [onClose]);

    const handleNameChange = useCallback((value = '') => {
        const hasSpecialCharsInName = removeUnallowedCharsFromString(value) !== value;
        const correctedAssetName = hasSpecialCharsInName ? removeUnallowedCharsFromString(value) ?? value : value;
        setAssetName(correctedAssetName);
        setSpecialCharError(hasSpecialCharsInName ? translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AddGearbox_NameSpecialChars') : '');
        if (!touched) {
            setTouched(true);
        }
    }, [touched, translate]);

    const handleOutputSpeedRatioChange = useCallback((value) => {
        let sanitized = (value || '').replaceAll(/[^0-9.]/g, '');
        // Allow only one dot
        const dotIndex = sanitized.indexOf('.');
        if (dotIndex !== -1) {
            sanitized = sanitized.slice(0, dotIndex + 1) + sanitized.slice(dotIndex + 1).replaceAll('.', '');
        }
        setOutputSpeedRatio(sanitized);
    }, []);

    return (
        <GeneralDialog
            show={show}
            close={handleClose}
            title={translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AddGearbox_Title')}
            closeButton={true}
            persistent={true}
            cancelButtonProps={{
                text: translate('ABB.Powertrain.Frontend.cancelLabel'),
                onClick: handleClose
            }}
            acceptButtonProps={{
                text: translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AddGearbox_AddButton'),
                onClick: handleAdd,
                disabled: !isValid
            }}
            modalClassName='add-gearbox-dialog-modal'
            dialogClassName='add-gearbox-dialog'
        >
            <div className='add-gearbox-dialog-content'>
                <div className='info-message'>
                    <svg width='16' height='16' viewBox='0 0 20 20' fill='#0078D4'>
                        <path fillRule='evenodd' d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z' clipRule='evenodd' />
                    </svg>
                    <span>
                        {translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AddGearbox_InfoMessage')}
                    </span>
                </div>

                {/* Asset name - mandatory */}
                <div className='form-field'>
                    <label className='field-label required'>
                        {translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AddGearbox_AssetNameLabel')}
                    </label>
                    <GeneralInput
                        id='gearbox-asset-name'
                        placeholder={translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AddGearbox_AssetNamePlaceholder')}
                        value={assetName}
                        onChange={handleNameChange}
                        maxLength={MAX_INPUT_CHAR_LENGTH}
                    />
                    {validationError &&
                        <div className='field-error'>{validationError}</div>
                    }
                </div>

                {/* Output speed ratio - optional */}
                <div className='form-field'>
                    <label className='field-label'>
                        {translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_OutputSpeedRatioLabel')}
                    </label>
                    <GeneralInput
                        id='gearbox-output-speed-ratio'
                        placeholder={translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AddGearbox_OutputSpeedRatioPlaceholder')}
                        value={outputSpeedRatio}
                        onChange={handleOutputSpeedRatioChange}
                        maxLength={MAX_SPEED_RATIO_LENGTH}
                    />
                </div>

                {/* Read-only pre-filled fields */}
                <div className='form-divider' />
                <div className='form-field'>
                    <label className='field-label'>
                        {translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AddGearbox_OrganizationLabel')}
                    </label>
                    <GeneralInput
                        id='gearbox-organization'
                        value={organization || ''}
                        disabled={true}
                    />
                </div>

                <div className='form-field'>
                    <label className='field-label'>
                        {translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AddGearbox_SiteLabel')}
                    </label>
                    <GeneralInput
                        id='gearbox-site'
                        value={site || ''}
                        disabled={true}
                    />
                </div>

                <div className='form-field'>
                    <label className='field-label'>
                        {translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AddGearbox_AssetTypeLabel')}
                    </label>
                    <GeneralInput
                        id='gearbox-asset-type'
                        value={ADD_GEARBOX_DEFAULTS.ASSET_TYPE_DISPLAY}
                        disabled={true}
                    />
                </div>

                <div className='form-field'>
                    <label className='field-label'>
                        {translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AddGearbox_SubTypeLabel')}
                    </label>
                    <GeneralInput
                        id='gearbox-sub-type'
                        value={ADD_GEARBOX_DEFAULTS.SUB_TYPE_DISPLAY}
                        disabled={true}
                    />
                </div>

                <div className='access-group-note'>
                    <IconInfo width={14} height={14} color={colors.doveGray} />
                    <span>
                        {translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AddGearbox_NoAccessGroupNote')}
                    </span>
                </div>
            </div>
        </GeneralDialog>
    );
};

AddGearboxDialog.propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onAdd: PropTypes.func.isRequired,
    organization: PropTypes.string,
    site: PropTypes.string
};

export default AddGearboxDialog;
